import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import config from '../../../config/config';
import { FaBars, FaTimes, FaBell, FaUser, FaSignOutAlt, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import Footer from '../../../components/common/Footer';
import '../../../styles/DashboardLayout.css';

const DashboardLayout = ({ children, menuItems = [], userRole = 'user' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        const saved = localStorage.getItem('sidebarOpen');
        return saved ? JSON.parse(saved) : false;
    });
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [justReadIds, setJustReadIds] = useState(new Set());
    
    const [expandedMenus, setExpandedMenus] = useState(() => {
        const saved = localStorage.getItem('expandedMenus');
        return saved ? JSON.parse(saved) : {};
    });

    const [userInfo, setUserInfo] = React.useState({
        fullname: '',
        username: '',
        email: ''
    });

    React.useEffect(() => {
        const getUserInfo = async () => {
            const token = localStorage.getItem('token');
            
            if (!token) {
                setUserInfo({
                    fullname: 'User',
                    username: 'user',
                    email: ''
                });
                return;
            }

            try {
                const response = await axios.get(`${config.API_URL}/profile`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                console.log('Profile response:', response.data);

                setUserInfo({
                    fullname: response.data.fullname || response.data.username || 'User',
                    username: response.data.username || 'user',
                    email: response.data.email || ''
                });
            } catch (error) {
                console.error('Failed to fetch profile:', error);
                const decoded = jwtDecode(token);
                setUserInfo({
                    fullname: decoded.username || 'User',
                    username: decoded.username || 'user',
                    email: ''
                });
            }
        };

        getUserInfo();
    }, []);

    React.useEffect(() => {
        localStorage.setItem('expandedMenus', JSON.stringify(expandedMenus));
    }, [expandedMenus]);

    React.useEffect(() => {
        localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
    }, [sidebarOpen]);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dashboard-header-item')) {
                setShowNotifications(false);
                setShowProfile(false);
            }
            
            // Close sidebar on mobile when clicking backdrop
            if (window.innerWidth <= 768 && sidebarOpen) {
                if (!event.target.closest('.dashboard-sidebar') && 
                    !event.target.closest('.dashboard-brand-toggle')) {
                    setSidebarOpen(false);
                }
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [sidebarOpen]);

    const toggleNotifications = () => {
        setShowNotifications(!showNotifications);
        setShowProfile(false);
    };

    const toggleProfile = () => {
        setShowProfile(!showProfile);
        setShowNotifications(false);
    };

    const toggleMenu = (menuLabel) => {
        if (!sidebarOpen) return;
        
        setExpandedMenus(prev => ({
            ...prev,
            [menuLabel]: !prev[menuLabel]
        }));
    };

    React.useEffect(() => {
        if (!sidebarOpen) {
            setExpandedMenus({});
        }
    }, [sidebarOpen]);

    React.useEffect(() => {
        let previousWidth = window.innerWidth;
        
        const handleResize = () => {
            const currentWidth = window.innerWidth;
            
            // Only close sidebar when resizing from desktop to mobile
            if (previousWidth > 768 && currentWidth <= 768 && sidebarOpen) {
                setSidebarOpen(false);
            }
            
            previousWidth = currentWidth;
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [sidebarOpen]);

    React.useEffect(() => {
        const fetchNotifications = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            try {
                const decoded = jwtDecode(token);
                const userId = decoded.userId;

                const response = await axios.get(`${config.API_URL}/notifications/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                setNotifications(response.data);
                const unread = response.data.filter(n => !n.is_read).length;
                setUnreadCount(unread);
            } catch (error) {
                console.error("Failed to fetch notifications:", error);
            }
        };

        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleNotificationClick = async (notification) => {
        const token = localStorage.getItem('token');
        try {
            if (!notification.is_read) {
                setJustReadIds(prev => new Set([...prev, notification.id]));

                await axios.post(`${config.API_URL}/notifications/read/${notification.id}`, {}, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                setUnreadCount(prev => prev - 1);
                setNotifications(prev => prev.map(n =>
                    n.id === notification.id ? { ...n, is_read: true } : n
                ));

                setTimeout(() => {
                    setJustReadIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(notification.id);
                        return newSet;
                    });
                }, 500);
            }

            navigate(notification.link_to);
            setShowNotifications(false);
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    };

    const getNotificationIcon = (type) => {
        switch(type) {
            case 'success': return { icon: 'fa-check-circle', class: 'success' };
            case 'warning': return { icon: 'fa-exclamation-triangle', class: 'warning' };
            case 'info': return { icon: 'fa-info-circle', class: 'info' };
            default: return { icon: 'fa-bell', class: 'info' };
        }
    };

    const isActive = (path) => {
        // Exact match untuk path tanpa parameter
        if (location.pathname === path) return true;
        
        // Handle path dengan dynamic routing
        // Jika di halaman detail (data/:no_tiket), highlight menu data list parent
        const pathMap = {
            '/kl/data': ['/kl/data/'],
            '/admin/data': ['/admin/verify/', '/supervisor/approval/'],
            '/admin/verified': ['/rekapdata/'],
            '/publikator/data': ['/publikator/data/'],
            '/publikator/catalog': ['/publikator/catalog'],
            '/management/catalog': ['/management/catalog'],
        };
        
        // Cek apakah current path cocok dengan pattern dari menu item
        if (pathMap[path]) {
            return pathMap[path].some(pattern => location.pathname.startsWith(pattern));
        }
        
        return false;
    };

    const isMenuActive = (menuItem) => {
        if (!menuItem.items) {
            return isActive(menuItem.path);
        }
        return menuItem.items.some(subItem => isActive(subItem.path));
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/');
    };

    return (
        <div className="dashboard-layout">
            <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
                <div className="dashboard-sidebar-header">
                    <div className="dashboard-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} title="Ke Halaman Utama">
                        <img src="/ksp.png" alt="KSP" className="dashboard-logo-full" />
                    </div>
                </div>

                <nav className="dashboard-nav">
                    {menuItems.map((item, index) => (
                        <div key={index} className="dashboard-nav-group">
                            {item.items ? (
                                <>
                                    <button
                                        className={`dashboard-nav-section-title ${isMenuActive(item) ? 'active' : ''}`}
                                        onClick={() => toggleMenu(item.label)}
                                    >
                                        <i className={`fas ${item.icon}`}></i>
                                        <span>{item.label}</span>
                                        <i className={`fas fa-chevron-${expandedMenus[item.label] ? 'down' : 'right'} dashboard-nav-chevron`}></i>
                                    </button>
                                    {expandedMenus[item.label] && item.items.map((subItem, subIndex) => (
                                        <button
                                            key={subIndex}
                                            className={`dashboard-nav-item dashboard-nav-subitem ${isActive(subItem.path) ? 'active' : ''}`}
                                            onClick={() => navigate(subItem.path)}
                                        >
                                            <i className={`fas ${subItem.icon}`}></i>
                                            <span>{subItem.label}</span>
                                            {subItem.badge && (
                                                <span className="dashboard-nav-badge">{subItem.badge}</span>
                                            )}
                                        </button>
                                    ))}
                                </>
                            ) : (
                                <button
                                    className={`dashboard-nav-item ${isActive(item.path) ? 'active' : ''}`}
                                    onClick={() => navigate(item.path)}
                                >
                                    <i className={`fas ${item.icon}`}></i>
                                    <span>{item.label}</span>
                                    {item.badge && (
                                        <span className="dashboard-nav-badge">{item.badge}</span>
                                    )}
                                </button>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="dashboard-sidebar-footer">
                    <button 
                        className="dashboard-nav-item"
                        onClick={handleLogout}
                    >
                        <i className="fas fa-sign-out-alt"></i>
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            <main className="dashboard-main">
                <header className="dashboard-header">
                    <div className="dashboard-header-left">
                        <button 
                            className="dashboard-brand-toggle"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            <i className={`fas fa-${sidebarOpen ? 'bars' : 'bars'} brand-icon-svg`}></i>
                            <span className="brand-text">Integrasi IGT</span>
                            <i className={`fas fa-chevron-${sidebarOpen ? 'left' : 'right'} brand-chevron`}></i>
                        </button>
                    </div>
                    
                    <div className="dashboard-header-right">
                        <div className="dashboard-header-item">
                            <button 
                                className="dashboard-icon-btn"
                                onClick={toggleNotifications}
                            >
                                <i className="fas fa-bell"></i>
                                {unreadCount > 0 && <span className="dashboard-badge">{unreadCount}</span>}
                            </button>
                            {showNotifications && (
                                <div className="dashboard-dropdown dashboard-notifications-dropdown">
                                    <div className="dashboard-dropdown-header">
                                        <h4>Notifikasi {unreadCount > 0 && `(${unreadCount} baru)`}</h4>
                                        <button className="dashboard-text-btn" onClick={() => setShowNotifications(false)}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                    <div className="dashboard-dropdown-body">
                                        {notifications.length > 0 ? (
                                            notifications.map(notif => {
                                                const iconData = getNotificationIcon(notif.notification_type);
                                                let classes = `dashboard-notification-item`;
                                                
                                                if (!notif.is_read) {
                                                    classes += ' unread';
                                                } else {
                                                    classes += ' read';
                                                }
                                                
                                                if (justReadIds.has(notif.id)) {
                                                    classes += ' just-read';
                                                }
                                                
                                                return (
                                                    <button 
                                                        key={notif.id}
                                                        className={classes}
                                                        onClick={() => handleNotificationClick(notif)}
                                                    >
                                                        <div className={`dashboard-notification-icon ${iconData.class}`}>
                                                            <i className={`fas ${iconData.icon}`}></i>
                                                        </div>
                                                        <div className="dashboard-notification-content">
                                                            <p className="dashboard-notification-title">{notif.message}</p>
                                                            <p className="dashboard-notification-time">
                                                                {new Date(notif.created_at).toLocaleString('id-ID')}
                                                            </p>
                                                        </div>
                                                    </button>
                                                );
                                            })
                                        ) : (
                                            <div className="dashboard-notification-empty">
                                                <i className="fas fa-bell-slash"></i>
                                                <p>Tidak ada notifikasi</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="dashboard-header-item">
                            <button 
                                className="dashboard-profile-btn"
                                onClick={toggleProfile}
                            >
                                <div className="dashboard-avatar">
                                    <i className="fas fa-user"></i>
                                </div>
                                <span className="dashboard-profile-name">
                                    {userInfo.fullname || 'User'}
                                </span>
                                <i className="fas fa-chevron-down"></i>
                            </button>
                            {showProfile && (
                                <div className="dashboard-dropdown dashboard-profile-dropdown">
                                    <div className="dashboard-dropdown-header">
                                        <div className="dashboard-profile-info">
                                            <div className="dashboard-avatar-large">
                                                <i className="fas fa-user"></i>
                                            </div>
                                            <div>
                                                <h4>{userInfo.fullname || 'User'}</h4>
                                                <span className="dashboard-role-badge">
                                                    {userRole === 'Admin' ? 'Verifikator' : userRole}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dashboard-dropdown-body">
                                        <button className="dashboard-dropdown-item" onClick={() => navigate('/profile')}>
                                            <i className="fas fa-user"></i>
                                            <span>Profile</span>
                                        </button>
                                        <div className="dashboard-dropdown-divider"></div>
                                        <button className="dashboard-dropdown-item" onClick={handleLogout}>
                                            <i className="fas fa-sign-out-alt"></i>
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <div className="dashboard-content">
                    {children}
                </div>
                
                <Footer />
            </main>
        </div>
    );
};

export default DashboardLayout;